'use client';

import { useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Polls `/api/settings-version` every 5 seconds. When the returned `version` string changes,
 * invalidates the `['players']` and `['adminBanner']` React Queries, causing any mounted
 * consumers to refetch immediately against the already-fresh Data Cache (admin writes fire
 * `revalidateTag` on those caches before clients would even see the version change here).
 *
 * The first poll records a baseline without invalidating so newly-mounted clients don't
 * refetch immediately on load.
 */
export function useSettingsVersion() {
  const queryClient = useQueryClient();
  const lastSeen = useRef<string | null>(null);

  useQuery({
    queryKey: ['settings-version'],
    queryFn: async () => {
      const res = await fetch('/api/settings-version');
      if (!res.ok) throw new Error(`settings-version failed: ${res.status}`);
      const { version } = (await res.json()) as { version: string };

      if (lastSeen.current !== null && lastSeen.current !== version) {
        queryClient.invalidateQueries({ queryKey: ['players'] });
        queryClient.invalidateQueries({ queryKey: ['adminBanner'] });
      }
      lastSeen.current = version;
      return version;
    },
    refetchInterval: 5000,
    staleTime: 0,
    gcTime: Infinity,
  });
}
